import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Layout } from "../components/Layout";
import { cachedGet, photoSrc, photoImageUrl } from "../lib/api";
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  X,
  Play,
  Pause,
  Maximize2,
} from "lucide-react";

export default function PhotosPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [startInAutoplay, setStartInAutoplay] = useState(false);

  useEffect(() => {
    let mounted = true;
    cachedGet("/photos", (data) => {
      if (!mounted) return;
      setPhotos(data);
      setLoading(false);
    })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const preventContext = useCallback((e) => e.preventDefault(), []);

  const openAt = (i, { autoplay = false } = {}) => {
    setStartInAutoplay(autoplay);
    setLightboxIndex(i);
  };

  return (
    <Layout>
      <section
        className="relative pt-28 md:pt-32 pb-24 px-5 md:px-8 photos-no-save"
        data-testid="photos-page"
        onContextMenu={preventContext}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-20 fade-up">
            <div className="seal mb-6">
              <Camera size={12} /> Golden Hour
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-5"
              data-testid="photos-heading"
            >
              Sunsets Over Vineyard
            </h1>
            <p className="text-white/60 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              Captured moments along Utah Lake — a tribute to the place we are
              proud to call home.
            </p>
            {photos.length > 1 && (
              <button
                type="button"
                onClick={() => openAt(0, { autoplay: true })}
                className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-400 text-[#04212f] hover:bg-yellow-300 transition-all font-medium text-sm shadow-[0_20px_60px_-20px_rgba(245,196,60,0.5)] active:scale-95"
                data-testid="photos-slideshow-btn"
              >
                <Play size={14} className="fill-current" />
                Start Slideshow
              </button>
            )}
          </div>

          {loading ? (
            <div
              className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [&>*]:mb-4 md:[&>*]:mb-6"
              data-testid="photos-loading"
            >
              {[320, 240, 380, 260, 340, 280].map((h, i) => (
                <div key={i} className="skeleton rounded-2xl break-inside-avoid" style={{ height: h }} />
              ))}
            </div>
          ) : photos.length === 0 ? (
            <div
              className="text-center py-20 glass rounded-2xl max-w-2xl mx-auto"
              data-testid="photos-empty"
            >
              <Camera className="mx-auto text-white/30 mb-4" size={40} />
              <p className="text-white/70 mb-1">No photos posted yet.</p>
              <p className="text-white/40 text-sm">Check back soon — new shots coming.</p>
            </div>
          ) : (
            <MasonryGrid
              photos={photos}
              onOpen={(i) => openAt(i)}
              preventContext={preventContext}
            />
          )}
        </div>
      </section>

      {lightboxIndex !== null && createPortal(
        <Lightbox
          photos={photos}
          index={lightboxIndex}
          autoplay={startInAutoplay}
          onClose={() => setLightboxIndex(null)}
          onChange={(i) => setLightboxIndex(i)}
        />, document.body
      )}
    </Layout>
  );
}

/* ----------------- Masonry grid ----------------- */
const MasonryGrid = ({ photos, onOpen, preventContext }) => (
  <div
    className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [&>*]:mb-4 md:[&>*]:mb-6"
    data-testid="photos-grid"
  >
    {photos.map((p, i) => (
      <PhotoThumb
        key={p.id}
        photo={p}
        index={i}
        onOpen={() => onOpen(i)}
        preventContext={preventContext}
      />
    ))}
  </div>
);

const PhotoThumb = ({ photo, index, onOpen, preventContext }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      onClick={onOpen}
      onContextMenu={preventContext}
      className={`relative w-full break-inside-avoid rounded-2xl overflow-hidden group cursor-zoom-in no-save hover-lift block ${
        loaded ? "bg-white/[0.04]" : "skeleton min-h-[220px]"
      }`}
      style={{ animationDelay: `${Math.min(index, 12) * 60}ms` }}
      data-testid={`photo-thumb-${photo.id}`}
    >
      <img
        src={photoSrc(photo)}
        alt={photo.caption || "Sunset photo"}
        // First screenful loads immediately; the rest as you scroll
        loading={index < 6 ? "eager" : "lazy"}
        fetchpriority={index < 3 ? "high" : "auto"}
        decoding="async"
        draggable={false}
        onContextMenu={preventContext}
        onLoad={() => setLoaded(true)}
        className={`w-full h-auto object-cover transition-all duration-300 ${
          loaded ? "opacity-100 scale-100" : "opacity-0 scale-[1.02]"
        } group-hover:scale-105 no-save`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <span className="photo-watermark">© COUNCILMAN WOOD</span>
      <span className="absolute inset-0 pointer-events-none" aria-hidden="true" />
    </button>
  );
};

/* ----------------- Lightbox ----------------- */
const SLIDESHOW_INTERVAL_MS = 4500;

const Lightbox = ({ photos, index, autoplay = false, onClose, onChange }) => {
  const total = photos.length;
  const photo = photos[index];
  const [direction, setDirection] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const imageRef = useRef(null);
  const [playing, setPlaying] = useState(autoplay);
  const [progress, setProgress] = useState(0);
  const touchRef = useRef({ x: 0, y: 0, t: 0 });
  const rootRef = useRef(null);
  const timerRef = useRef(null);
  const progressTickRef = useRef(null);

  const go = useCallback(
    (delta) => {
      setDirection(delta);
      setImgLoaded(false);
      setImgError(false);
      setProgress(0);
      onChange((index + delta + total) % total);
    },
    [index, total, onChange]
  );

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === " ") {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.classList.add("lightbox-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      document.body.classList.remove("lightbox-open");
    };
  }, [go, onClose]);

  // Cached images can finish before React attaches its load listener.
  useEffect(() => {
    const img = imageRef.current;
    if (img?.complete && img.naturalWidth > 0) setImgLoaded(true);
  }, [photo.id]);

  // Preload neighbours
  useEffect(() => {
    [1, -1].forEach((d) => {
      const nb = photos[(index + d + total) % total];
      if (nb) {
        const im = new Image();
        im.src = nb.image_url || photoImageUrl(nb.id);
      }
    });
  }, [index, photos, total]);

  // Auto-fullscreen when autoplay starts
  useEffect(() => {
    if (!autoplay) return;
    const el = rootRef.current;
    if (el && el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    }
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slideshow timer — wait until the current image actually loads before
  // starting the countdown so users see each photo for the full interval.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressTickRef.current) clearInterval(progressTickRef.current);
    setProgress(0);
    if (!playing || !imgLoaded) return;

    timerRef.current = setTimeout(() => go(1), SLIDESHOW_INTERVAL_MS);
    const start = Date.now();
    progressTickRef.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - start) / SLIDESHOW_INTERVAL_MS) * 100);
      setProgress(pct);
    }, 80);

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(progressTickRef.current);
    };
  }, [playing, imgLoaded, index, go]);

  // Touch swipe
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) && dt < 800) {
      go(dx < 0 ? 1 : -1);
    }
  };

  const stop = (e) => e.stopPropagation();
  const block = (e) => e.preventDefault();

  const toggleFullscreen = () => {
    const el = rootRef.current;
    if (!document.fullscreenElement) {
      el?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[100] flex items-center justify-center photos-no-save"
      onClick={onClose}
      onContextMenu={block}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Sunset photo viewer"
      data-testid="lightbox"
    >
      <div className="absolute inset-0 bg-[#04212f]/95 backdrop-blur-2xl" />

      {/* Slideshow progress bar */}
      {playing && (
        <div
          className="absolute top-0 left-0 right-0 h-[3px] bg-white/10 z-30"
          aria-hidden="true"
        >
          <div
            className="h-full bg-yellow-400 transition-[width] duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Top toolbar — top-LEFT on mobile so it doesn't collide with the
          floating hamburger menu in the top-right. On desktop the close
          button sits in the top-right corner. */}
      <div
        className="absolute top-4 left-4 right-4 md:top-7 md:left-auto md:right-7 z-30 flex items-center justify-between md:justify-end gap-3 pointer-events-none"
        onClick={stop}
      >
        <div className="md:hidden text-white/75 text-[10px] tracking-[0.35em] uppercase font-medium pointer-events-auto">
          {String(index + 1).padStart(2, "0")}
          <span className="mx-1.5 text-yellow-400/70">/</span>
          {String(total).padStart(2, "0")}
        </div>

        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-yellow-400/20 border border-white/15 hover:border-yellow-400/50 text-white hover:text-yellow-400 transition-all flex items-center justify-center backdrop-blur"
            aria-label={playing ? "Pause slideshow" : "Play slideshow"}
            data-testid="lightbox-playpause"
          >
            {playing ? <Pause size={18} /> : <Play size={18} className="fill-current ml-0.5" />}
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            className="hidden sm:flex w-11 h-11 rounded-full bg-white/10 hover:bg-yellow-400/20 border border-white/15 hover:border-yellow-400/50 text-white hover:text-yellow-400 transition-all items-center justify-center backdrop-blur"
            aria-label="Toggle fullscreen"
            data-testid="lightbox-fullscreen"
          >
            <Maximize2 size={18} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-red-500/20 border border-white/15 hover:border-red-400/60 text-white hover:text-red-300 transition-all flex items-center justify-center backdrop-blur"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Counter (desktop) */}
      <div className="hidden md:block absolute top-7 left-1/2 -translate-x-1/2 z-20 text-white/55 text-[11px] tracking-[0.35em] uppercase font-medium">
        {String(index + 1).padStart(2, "0")}
        <span className="mx-2 text-yellow-400/70">/</span>
        {String(total).padStart(2, "0")}
      </div>

      {/* Prev/Next (desktop) */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/8 hover:bg-yellow-400/15 border border-white/15 hover:border-yellow-400/50 text-white hover:text-yellow-400 transition-all items-center justify-center backdrop-blur"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/8 hover:bg-yellow-400/15 border border-white/15 hover:border-yellow-400/50 text-white hover:text-yellow-400 transition-all items-center justify-center backdrop-blur"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      {!imgLoaded && !imgError && <p className="absolute z-10 text-white/70 text-sm" role="status">Loading photo…</p>}
      {imgError && <div className="absolute z-10 text-center p-6"><p className="text-white/80 mb-4">This photo could not be loaded.</p><a className="btn-ghost inline-flex px-6 py-3" href={photo.image_url || photoImageUrl(photo.id)} target="_blank" rel="noopener noreferrer">Open photo</a></div>}
      {/* Image */}
      <div
        key={photo.id}
        className="relative max-w-[94vw] max-h-[78vh] md:max-h-[86vh] flex items-center justify-center"
        onClick={stop}
      >
        <img
          ref={imageRef}
          src={photo.image_url || photoImageUrl(photo.id)}
          alt={photo.caption || "Photo"}
          draggable={false}
          onContextMenu={block}
          onDragStart={block}
          loading="eager"
          decoding="async"
          onError={() => setImgError(true)}
          onLoad={() => { setImgLoaded(true); setImgError(false); }}
          className={`max-w-[94vw] max-h-[78vh] md:max-h-[86vh] object-contain rounded-xl no-save shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] select-none ${
            imgLoaded
              ? "opacity-100 translate-x-0 scale-100"
              : `opacity-0 ${direction > 0 ? "translate-x-3" : direction < 0 ? "-translate-x-3" : ""} scale-[0.99]`
          } transition-all duration-300 ease-out`}
          data-testid="lightbox-image"
        />
        <span
          className="photo-watermark"
          style={{ fontSize: "0.85rem", right: "16px", bottom: "14px" }}
        >
          © COUNCILMAN WOOD
        </span>
        <span className="absolute inset-0 pointer-events-none" aria-hidden="true" />
      </div>

      {/* Mobile bottom controls (prev / dot pager / next) */}
      {total > 1 && (
        <div
          className="md:hidden absolute bottom-6 left-0 right-0 z-30 flex items-center justify-center gap-5 pointer-events-none"
          onClick={stop}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            className="pointer-events-auto w-12 h-12 rounded-full bg-white/10 hover:bg-yellow-400/20 border border-white/15 text-white transition-all flex items-center justify-center backdrop-blur"
            aria-label="Previous"
            data-testid="lightbox-prev-mobile"
          >
            <ChevronLeft size={22} />
          </button>
          <span className="pointer-events-auto text-white/60 text-xs tracking-[0.25em]">
            SWIPE
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            className="pointer-events-auto w-12 h-12 rounded-full bg-white/10 hover:bg-yellow-400/20 border border-white/15 text-white transition-all flex items-center justify-center backdrop-blur"
            aria-label="Next"
            data-testid="lightbox-next-mobile"
          >
            <ChevronRight size={22} />
          </button>
        </div>
      )}
    </div>
  );
};
